import React, { useState, useEffect } from "react";
import {
  Paper,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";

const roomConfig = {
  bedroom: {
    label: "Miegamasis",
    enabledFields: { area: true, people: true },
    defaultValues: { supplyPerM2: 2.5, supplyPerPerson: 30 },
  },
  children_room: {
    label: "Vaikų kambarys",
    enabledFields: { area: true, people: true },
    defaultValues: { supplyPerM2: 2.5, supplyPerPerson: 30 },
  },
  living_room: {
    label: "Svetainė",
    enabledFields: { area: true, people: true },
    defaultValues: { supplyPerM2: 1.8, supplyPerPerson: 25 },
  },
  dining_room: {
    label: "Valgomasis",
    enabledFields: { area: true, people: true },
    defaultValues: { supplyPerM2: 1.8, supplyPerPerson: 25 },
  },
  living_and_dining: {
    label: "Svetainė su valgomuoju (Virtuvė neįskaičiuota)",
    enabledFields: { area: true, people: true },
    defaultValues: { supplyPerM2: 1.8, supplyPerPerson: 25 },
  },
  home_office: {
    label: "Darbo kambarys",
    enabledFields: { area: true, people: true },
    defaultValues: { supplyPerM2: 2.5, supplyPerPerson: 30 },
  },
  guest_room: {
    label: "Svečių kambarys",
    enabledFields: { area: true, people: true },
    defaultValues: { supplyPerM2: 1.8, supplyPerPerson: 30 },
  },
  other_supply: {
    label: "Kitas (padavimas)",
    enabledFields: { area: true },
    defaultValues: { supplyPerM2: 1.8 },
  },
  kitchen: {
    label: "Virtuvė",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 72 },
  },
  restroom_combined: {
    label: "WC kartu su vonia/dušu",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 54 },
  },
  restroom: {
    label: "WC",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 36 },
  },
  shower: {
    label: "Vonios/dušo patalpa",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 54 },
  },
  laundry_room: {
    label: "Skalbykla",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 30, exhaustPerM2: 3.6 },
  },
  vestibule: {
    label: "Tambūras",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 15 },
  },
  storage_room: {
    label: "Sandėliukas/drabužinė",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 15 },
  },
  corridor: {
    label: "Koridorius",
  },
  technical_room: {
    label: "Techninė patalpa",
    enabledFields: { area: true },
    defaultValues: { exhaustPerRoom: 15 },
  },
  other_exhaust: {
    label: "Kitas (ištraukimas)",
    enabledFields: { area: true },
    defaultValues: { exhaustPerM2: 1.8 },
  },
};

const getEnabledFields = (type) => {
  return roomConfig[type]?.enabledFields || {};
};

const getDefaultValuesForRoomType = (type) => {
  const defaultValues = {
    name: "",
    area: 0,
    people: 0,
    supply: 0,
    exhaust: 0,
    supplyPerM2: 0,
    supplyPerPerson: 0,
    exhaustPerM2: 0,
    exhaustPerRoom: 0,
    supplyOrigin: 0,
    exhaustOrigin: 0,
  };

  return {
    ...defaultValues,
    ...(roomConfig[type]?.defaultValues || {}),
    name: roomConfig[type]?.label || "",
  };
};

function RoomTable({ updateRoomTableData }) {
  const [rows, setRows] = useState([
    { type: "", area: 0, people: 0, supply: 0, exhaust: 0 },
  ]);

  const addRow = () => {
    setRows([...rows, { type: "", area: 0, people: 0, supply: 0, exhaust: 0 }]);
  };

  // copied method. maybe move to not dupicate
  const getTotal = (rows, field) => {
    return rows.reduce((total, row) => {
      const value = parseFloat(row[field]);
      return total + (isNaN(value) ? 0 : value);
    }, 0);
  };

  const recalculateRow = (row) => {
    const updatedRow = { ...row };

    if (row.supplyPerM2) {
      updatedRow.supplyOrigin = Math.round(
        Math.max(
          parseFloat(row.supplyPerM2) * (parseFloat(row.area) || 0),
          parseFloat(row.supplyPerPerson) * (parseFloat(row.people) || 0)
        )
      );
    }

    if (row.exhaustPerRoom || row.exhaustPerM2) {
      updatedRow.exhaustOrigin = Math.round(
        Math.max(
          parseFloat(row.exhaustPerM2) * (parseFloat(row.area) || 0),
          parseFloat(row.exhaustPerRoom)
        )
      );
    }

    return updatedRow;
  };

  const compensateRows = (rows) => {
    const totalSupplyOrigin = getTotal(rows, "supplyOrigin");
    const totalExhaustOrigin = getTotal(rows, "exhaustOrigin");

    const coefficientExhaust =
      totalExhaustOrigin > 0 && totalSupplyOrigin > totalExhaustOrigin
        ? totalSupplyOrigin / totalExhaustOrigin
        : 1;
    const coefficientSupply =
      totalSupplyOrigin > 0 && totalExhaustOrigin > totalSupplyOrigin
        ? totalExhaustOrigin / totalSupplyOrigin
        : 1;

    return rows.map((row) => ({
      ...row,
      supply: row.type ? Math.round(row.supplyOrigin * coefficientSupply) : 0,
      exhaust: row.type
        ? Math.round(row.exhaustOrigin * coefficientExhaust)
        : 0,
    }));
  };

  const handleChange = (index, field, value) => {
    let newRows = [...rows];

    if (field === "type") {
      const defaultValues = getDefaultValuesForRoomType(value);
      newRows[index] = {
        ...newRows[index],
        ...defaultValues,
        [field]: value,
      };
    } else {
      newRows[index][field] = value;
    }

    newRows[index] = recalculateRow(newRows[index]);
    newRows = compensateRows(newRows);

    setRows(newRows);
  };

  const handleRemove = (index) => {
    let newRows = rows.filter((_, i) => i !== index);
    newRows = compensateRows(newRows);
    setRows(newRows);
  };

  useEffect(() => {
    updateRoomTableData({ rows });
  }, [rows, updateRoomTableData]);

  const handleFocus = (event) => {
    event.target.select();
  };

  return (
    <Box 
      sx={{
        p: 2,
        pt: 0,
        "@media (max-width: 600px)": {
          p: 1,
          pt: 0,
        },
      }}
    >
      <Paper elevation={2} sx={{ 
        p: 2,
        "@media (max-width: 600px)": {
          p: 1,
        },
      }}>
        <Typography variant="h6" gutterBottom>
          Patalpos
        </Typography>

        {rows.map((row, index) => {
          const enabledFields = getEnabledFields(row.type);
          const isLastRow = rows.length <= 1;
          return (
            <Grid 
              container 
              key={index} 
              alignItems="center"
              sx={{
                p: 2,
                "@media (max-width: 600px)": {
                  p: 1,
                  "& .MuiGrid-item": {
                    paddingLeft: "8px",
                    paddingTop: "8px",
                  },
                },
                "@media (min-width: 601px)": {
                  "& .MuiGrid-item": {
                    paddingLeft: "16px",
                    paddingTop: "16px",
                  },
                },
              }}
              spacing={{ xs: 1, sm: 2 }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">
                    Patalpos tipas
                  </InputLabel>
                  <Select
                    value={row.type}
                    name="type"
                    onChange={(e) =>
                      handleChange(index, "type", e.target.value)
                    }
                    fullWidth
                    sx={{ minWidth: 200 }}
                    label="Patalpos tipas"
                    MenuProps={{
                      disablePortal: true,
                      autoFocus: false,
                      disableAutoFocusItem: true,
                      disableEnforceFocus: true,
                      disableAutoFocus: true,
                      disableScrollLock: true,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    }}
                  >
                    {Object.entries(roomConfig).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  value={row.area}
                  name="area"
                  type="number"
                  onChange={(e) => handleChange(index, "area", e.target.value)}
                  fullWidth
                  disabled={!enabledFields.area}
                  inputProps={{ min: 0, step: "0.01" }}
                  label="Patalpos plotas (m²)"
                  inputMode="numeric"
                  onFocus={handleFocus}
                  onWheel={(e) => e.target.blur()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  value={row.people}
                  name="people"
                  type="number"
                  onChange={(e) =>
                    handleChange(index, "people", e.target.value)
                  }
                  fullWidth
                  disabled={!enabledFields.people}
                  inputProps={{ min: 0 }}
                  label="Žmonių kiekis"
                  inputMode="numeric"
                  onFocus={handleFocus}
                  onWheel={(e) => e.target.blur()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  value={row.supply}
                  name="supply"
                  type="number"
                  onChange={(e) =>
                    handleChange(index, "supply", e.target.value)
                  }
                  fullWidth
                  disabled={!enabledFields.supply}
                  inputProps={{ min: 0 }}
                  label="Oro padavimas (m³/h)"
                  inputMode="numeric"
                  onFocus={handleFocus}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      fontWeight: "bold",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  value={row.exhaust}
                  name="exhaust"
                  type="number"
                  onChange={(e) =>
                    handleChange(index, "exhaust", e.target.value)
                  }
                  fullWidth
                  disabled={!enabledFields.exhaust}
                  inputProps={{ min: 0 }}
                  label="Oro ištraukimas (m³/h)"
                  inputMode="numeric"
                  onFocus={handleFocus}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      fontWeight: "bold",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1} align="center">
                <Tooltip
                  title={
                    isLastRow
                      ? "Negalima ištrinti paskutinio įrašo"
                      : "Ištrinti įrašą"
                  }
                >
                  <span>
                    <IconButton
                      onClick={() => handleRemove(index)}
                      disabled={isLastRow}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          );
        })}

        <Box 
          sx={{
            p: 2,
            "@media (max-width: 600px)": {
              p: 1,
            },
          }}
        >
          <Button onClick={addRow} fullWidth variant="contained" size="medium">
            + Pridėti kambarį
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default RoomTable;
